import React, { Children, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const texteOption = (value) => {
  if (value == null || typeof value === 'boolean') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(texteOption).join('');
  return '';
};

const lireOptions = (children, groupe = null, resultat = []) => {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'option') {
      resultat.push({
        value: String(child.props.value ?? ''),
        label: child.props.children,
        text: texteOption(child.props.children),
        disabled: Boolean(child.props.disabled),
        hidden: Boolean(child.props.hidden),
        group: groupe,
      });
      return;
    }
    if (child.type === 'optgroup') {
      lireOptions(child.props.children, child.props.label || null, resultat);
      return;
    }
    if (child.type === React.Fragment) lireOptions(child.props.children, groupe, resultat);
  });
  return resultat;
};

export default function ErpiaSelect({
  children,
  value,
  defaultValue = '',
  onChange,
  onBlur,
  onFocus,
  onClick,
  name,
  id,
  className = '',
  style,
  disabled = false,
  required = false,
  title,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}) {
  const generatedId = useId();
  const selectId = id || generatedId;
  const options = useMemo(() => lireOptions(children), [children]);
  const controlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(String(defaultValue ?? ''));
  const currentValue = String(controlled ? value ?? '' : internalValue);
  const selected = options.find((option) => option.value === currentValue && !option.hidden)
    || options.find((option) => !option.hidden);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const enabledIndexes = useMemo(
    () => options.map((option, index) => (!option.disabled && !option.hidden ? index : -1)).filter((index) => index >= 0),
    [options],
  );

  const positionner = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Déclencheur sorti de l'écran (scroll, panneau qui se referme) : le menu
    // se ferme au lieu de rester orphelin au milieu de la page.
    if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
      setOpen(false);
      return;
    }
    const espaceBas = window.innerHeight - rect.bottom;
    const ouvrirHaut = espaceBas < 220 && rect.top > espaceBas;
    const hauteur = Math.min(320, Math.max(150, (ouvrirHaut ? rect.top : espaceBas) - 16));
    const largeur = Math.max(rect.width, 180);
    // Ouvert vers le haut, le menu s'accroche par son BAS au déclencheur :
    // placé par son haut avec la hauteur MAXIMALE, un menu de trois options
    // flottait au milieu du tableau, loin de son bouton (vécu 27.10, « 10 /
    // page » de la liste des besoins).
    setMenuStyle({
      position: 'fixed',
      left: Math.max(8, Math.min(rect.left, window.innerWidth - largeur - 8)),
      ...(ouvrirHaut
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
      width: largeur,
      maxHeight: hauteur,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const selectedIndex = options.findIndex(
      (option) => option.value === currentValue && !option.disabled && !option.hidden,
    );
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : (enabledIndexes[0] ?? 0));
    positionner();
  }, [open, options, currentValue, enabledIndexes, positionner]);

  useEffect(() => {
    if (!open) return undefined;
    const fermerDehors = (event) => {
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpen(false);
        onBlur?.(event);
      }
    };
    const replacer = () => positionner();
    // EN CAPTURE, et c'est tout le sujet. Le déclencheur d'un sélecteur coupe
    // la propagation du mousedown (plus bas : un sélecteur posé dans une ligne
    // cliquable ne doit pas ouvrir la fiche). Écouté en phase de bulle, ce
    // handler ne voyait donc JAMAIS le clic sur le déclencheur d'un AUTRE
    // sélecteur : les menus s'empilaient à l'écran, ouverts tous en même temps
    // et se recouvrant (vécu 09.09 sur les filtres des besoins). La capture
    // passe avant la coupure : le menu déjà ouvert se ferme, puis l'autre
    // s'ouvre — un seul menu à la fois, sans registre global ni contexte.
    document.addEventListener('mousedown', fermerDehors, true);
    window.addEventListener('resize', replacer);
    window.addEventListener('scroll', replacer, true);
    return () => {
      document.removeEventListener('mousedown', fermerDehors, true);
      window.removeEventListener('resize', replacer);
      window.removeEventListener('scroll', replacer, true);
    };
  }, [open, onBlur, positionner]);

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  const choisir = (option) => {
    if (!option || option.disabled) return;
    if (!controlled) setInternalValue(option.value);
    const target = { value: option.value, name, id: selectId };
    onChange?.({ type: 'change', target, currentTarget: target });
    setOpen(false);
    triggerRef.current?.focus();
  };

  const deplacer = (direction) => {
    if (!enabledIndexes.length) return;
    const position = enabledIndexes.indexOf(activeIndex);
    const base = position < 0 ? 0 : position;
    const suivant = (base + direction + enabledIndexes.length) % enabledIndexes.length;
    setActiveIndex(enabledIndexes[suivant]);
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    if (!open && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); deplacer(1); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); deplacer(-1); }
    else if (event.key === 'Home') { event.preventDefault(); setActiveIndex(enabledIndexes[0] ?? 0); }
    else if (event.key === 'End') { event.preventDefault(); setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? 0); }
    else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choisir(options[activeIndex]); }
    else if (event.key === 'Escape' || event.key === 'Tab') { setOpen(false); }
  };

  let dernierGroupe = null;
  const menu = open && typeof document !== 'undefined' ? createPortal(
    <div
      ref={menuRef}
      className="erp-select__menu"
      role="listbox"
      id={`${selectId}-listbox`}
      style={menuStyle}
      aria-labelledby={ariaLabelledby}
      // Le menu est rendu dans document.body, mais un portail propage ses
      // événements dans l'arbre React : sans cette coupure, choisir une option
      // déclenche aussi le onClick de la ligne d'origine.
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {options.map((option, index) => {
        if (option.hidden) return null;
        const afficherGroupe = option.group && option.group !== dernierGroupe;
        dernierGroupe = option.group;
        return (
          <React.Fragment key={`${option.value}-${index}`}>
            {afficherGroupe && <div className="erp-select__group">{option.group}</div>}
            <button
              type="button"
              role="option"
              aria-selected={option.value === currentValue}
              disabled={option.disabled}
              className={`erp-select__option${option.value === currentValue ? ' is-selected' : ''}${index === activeIndex ? ' is-active' : ''}`}
              onMouseEnter={() => !option.disabled && setActiveIndex(index)}
              onClick={() => choisir(option)}
            >
              <span>{option.label}</span>
              {option.value === currentValue && <i className="bi bi-check2" aria-hidden="true" />}
            </button>
          </React.Fragment>
        );
      })}
    </div>,
    document.body,
  ) : null;

  return (
    <span className="erp-select" title={title}>
      {name && <input type="hidden" name={name} value={currentValue} />}
      <button
        ref={triggerRef}
        type="button"
        id={selectId}
        className={`erp-select__trigger ${className}`.trim()}
        style={style}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${selectId}-listbox`}
        aria-required={required || undefined}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        // Un sélecteur posé dans une ligne cliquable ne doit JAMAIS activer la
        // ligne : changer un statut n'est pas ouvrir la fiche. La coupure se
        // fait ici, une fois, plutôt que dans chaque écran qui l'oublie.
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.(event);
          if (!disabled) setOpen((current) => !current);
        }}
      >
        <span className="erp-select__value">{selected?.label || 'Sélectionner'}</span>
        <i className={`bi bi-chevron-down erp-select__chevron${open ? ' is-open' : ''}`} aria-hidden="true" />
      </button>
      {menu}
    </span>
  );
}
